import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useAuth, getToken } from '../../hooks/useAuth';
import { useParams, useNavigate } from 'react-router-dom';
import { CIcon } from '@coreui/icons-react';
import axios from 'axios';
import apiClient from '../../utils/apiClient';
import "leaflet/dist/leaflet.css";
import '../../index.css';
import { clearDataCache } from '../../utils/cacheManager';
import Widgets from '../widgets/Widgets';
import * as XLSX from 'xlsx';
import Tooltip from "../Tooltip/tooltip";
import {
  CCard, CButton, CTab, CTabs, CTabList, CTabContent, CTabPanel, CFormInput, CFormLabel,
  CCol, CRow, CCardBody, CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody,
  CTableDataCell, CDropdown, CDropdownToggle, CDropdownMenu, CDropdownItem, CModal,
  CModalHeader, CModalTitle, CModalBody, CModalFooter, CFormSelect, CModalContent,
  CTooltip, CFormTextarea, CToast, CToastBody, CToastClose, CSpinner, CAlertLink,
  CPagination, CPaginationItem, CPlaceholder,
} from '@coreui/react';
import { cilMagnifyingGlass, cilMap, cilOptions, cilCloudDownload, cilCompress, cilArrowLeft, cilThumbUp, cilThumbDown, cilMeh, cilSave, cilCheckCircle, cilReload } from '@coreui/icons';
import iconArrowDown from '../../assets/images/arrow-down.png';
import BuscadorSenales from './BuscadorSenales';

// New Modular Components
import StatsCards from './DetailComponents/StatsCards';
import MapSection from './DetailComponents/MapSection';
import ProjectInfoCard from './DetailComponents/ProjectInfoCard';
import DetectionsTable from './DetailComponents/DetectionsTable';
import { OrangeIcon, greenIcon, redIcon, yellowIcon, VioletIcon, getIcon, isValidCoordinate, calculatePriorityResult, calculateAssignedCompany, getPriorityTheme } from './DetailComponents/utils';
import { getCategoryConfig, getCategoryByItem } from './CategoryConfig';
import ExecutiveSummary from './DetailComponents/ExecutiveSummary';
import { StatsSkeleton, TableSkeleton, InfoSkeleton } from './SkeletonDetails';
import FeedbackSemaphore from './DetailComponents/FeedbackSemaphore';
import UnifiedDetailModal from './DetailComponents/UnifiedDetailModal';
import JsonDataRenderer from './DetailComponents/JsonDataRenderer';

// --- Componente Optimizado para Visor de Imágenes ---
const ImageZoomViewer = ({ src }) => {
  const [zoom, setZoom] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [startPan, setStartPan] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  const zoomIn = () => setZoom((z) => Math.min(z + 0.5, 4));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.5, 0.5));
  const resetZoom = () => { setZoom(1); setTranslate({ x: 0, y: 0 }); };

  const clampTranslate = useCallback((x, y, currentZoom) => {
    const c = containerRef.current;
    const img = imgRef.current;
    if (!c || !img) return { x, y };

    const containerW = c.clientWidth;
    const containerH = c.clientHeight;
    const imgW = img.naturalWidth || img.clientWidth;
    const imgH = img.naturalHeight || img.clientHeight;

    const ratio = Math.min(containerW / imgW, containerH / imgH);
    const displayedW = imgW * ratio;
    const displayedH = imgH * ratio;

    const scaledW = displayedW * currentZoom;
    const scaledH = displayedH * currentZoom;

    let maxX = 0;
    let maxY = 0;

    if (scaledW > containerW) maxX = (scaledW - containerW) / 2;
    if (scaledH > containerH) maxY = (scaledH - containerH) / 2;

    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y))
    };
  }, []);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isPanning) return;
      const x = e.clientX - startPan.x;
      const y = e.clientY - startPan.y;
      setTranslate(clampTranslate(x, y, zoom));
    };

    const onMouseUp = () => setIsPanning(false);

    if (isPanning) {
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [isPanning, startPan, zoom, clampTranslate]);

  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      if (e.deltaY < 0) zoomIn();
      else zoomOut();
    }
  };

  return (
    <div
      ref={containerRef}
      className="zoom-viewer-container"
      onWheel={handleWheel}
      onMouseDown={(e) => {
        if (zoom <= 1) return;
        e.preventDefault();
        setIsPanning(true);
        setStartPan({ x: e.clientX - translate.x, y: e.clientY - translate.y });
      }}
      style={{ cursor: zoom > 1 ? (isPanning ? 'grabbing' : 'grab') : 'default' }}
    >
      <img
        ref={imgRef}
        src={src}
        alt="Detección"
        className="zoom-image-element"
        style={{
          transform: `translate3d(${translate.x}px, ${translate.y}px, 0) scale(${zoom})`,
          transition: isPanning ? 'none' : 'transform 0.2s cubic-bezier(0.2, 0, 0.2, 1)',
        }}
        draggable={false}
      />
      <div className="zoom-controls-overlay">
        <CButton color="light" variant="outline" className="zoom-btn" onClick={resetZoom}>1x</CButton>
        <CButton color="light" variant="outline" className="zoom-btn" onClick={zoomOut}>−</CButton>
        <CButton color="light" variant="outline" className="zoom-btn" onClick={zoomIn}>+</CButton>
        <div className="d-flex align-items-center px-2 text-white small fw-bold" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
          {Math.round(zoom * 100)}%
        </div>
      </div>
    </div>
  );
};

export default function DetailIndex({ profile, setProfile }) {

  const navigate = useNavigate();
  const { token } = useAuth();
  const { id } = useParams();
  const copyTimeoutRef = useRef(null);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  }, []);

  // Limpieza de memoria al desmontar
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const [dataProyecto, setDataProyecto] = useState(null);
  const [dataResults, setDataResults] = useState([]);
  const [dataResults2, setDataResults2] = useState([]);
  const [markersData, setMarkersData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [claseSeleccionada, setClaseSeleccionada] = useState('basura'); // Estado para el menú desplegable de clase
  const [mapFilter, setMapFilter] = useState('basura'); // 'arbol' | 'alumbrado' | 'semaforo' | 'basura' | 'baches'

  const handlefind = useCallback((type, externalSignal) => {
    const controller = externalSignal ? { signal: externalSignal } : new AbortController();

    const fetchData = async () => {
      const selectedType = type || claseSeleccionada || 'basura';

      // 1. Intentar cargar desde cache para velocidad (Stale-While-Revalidate)
      const cacheKey = `project_cache_${id}_${selectedType}`;
      const cachedData = localStorage.getItem(cacheKey);
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          setDataProyecto(parsed);
          setDataResults(parsed.result?.results || parsed.senales || []);
          setDataResults2(parsed.result?.results_2 || []);
          setMarkersData(parsed);
        } catch (e) {
          console.error("Error parseando cache", e);
        }
      }

      const url = (selectedType && selectedType !== 'all')
        ? `/api/detail-project/${id}/${selectedType}`
        : `/api/detail-project/${id}`;

      setIsLoading(true);
      try {
        const response = await apiClient.get(url, { signal: controller.signal });
        const data = response.data;

        // 2. Guardar en cache solo si el tamaño es razonable (aprox < 3MB para evitar QuotaExceeded)
        try {
          const jsonString = JSON.stringify(data);
          if (jsonString.length < 3000000) {
            localStorage.setItem(cacheKey, jsonString);
          } else {
            console.warn("Payload demasiado grande para cache (localStorage). Cargando solo en memoria.");
            localStorage.removeItem(cacheKey);
          }
        } catch (e) {
          console.warn("No se pudo guardar en cache:", e);
        }

        setDataProyecto(data);

        // Diferir actualizaciones secundarias para no bloquear el render inicial
        setTimeout(() => {
          if (controller.signal?.aborted) return;
          const results = data.result?.results || data.senales || [];
          const results2 = data.result?.results_2 || [];
          setDataResults(results);
          setDataResults2(results2);
          setMarkersData(data);
        }, 100);

        setFetchError(null);
      } catch (error) {
        if (error.name === 'CanceledError' || error.name === 'AbortError' || axios.isCancel(error)) {
          console.log('Request canceled:', url);
        } else {
          console.error('Error durante la solicitud:', error);
          setFetchError(error?.response?.status ? `Error ${error.response.status}` : 'Error de conexión');
        }
      } finally {
        if (!controller.signal?.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
    return () => { if (!externalSignal) controller.abort(); };
  }, [id, token, claseSeleccionada]);

  const handleDownload = () => {
    if (dataProyecto && (dataProyecto.senales || dataProyecto.result?.results)) {
      const baseData = dataProyecto.senales || dataProyecto.result.results;

      const dataToExport = baseData.map(item => {
        const parts = item.direccion ? item.direccion.split(',') : [];
        const direcc1 = parts[0] ? parts[0].trim() : '';
        const direcc2 = parts[1] ? parts[1].trim() : '';
        const direcc3 = parts.slice(2).join(',').trim();

        const prioridad = calculatePriorityResult(item);
        const empresaAsignada = calculateAssignedCompany(item);
        const jd = item?.json_data || {};

        const formatSi = (val) => (val === '1' || val === 1) ? 'Sí' : '';

        const exportedItem = {
          'ID alerta': item.id,
          'Prioridad': prioridad,
          'Empresa asignada': empresaAsignada,
          'Detección específica': item.tipo_especifico,
          'Direccion': direcc1,
          'Localidad 1': direcc2,
          'Localidad 2': direcc3,
          'Fecha': item.fecha_captura || item.fecha_creacion,
          'Observaciones': jd.observaciones || item.observaciones,
          'Descripción General': item.descripcion,
          'MAPS': `https://www.google.com/maps?q=${item.latitud},${item.longitud}`,
          'Ubicación Especial': jd.ubicacion_especial || item.ubicacion_especial,
          'En Calzada': jd.en_calzada || item.en_calzada,
          'Obstrucción Calzada': jd.obstruccion_calzada || item.obstruccion_calzada,
          'Tamaño Montículo': jd.monticulo_tamano || item.monticulo_tamano,
          'Cant. Estimada Bolsas': jd.estimacion_bolsas_cant || item.estimacion_bolsas_cant,
          'Bolsas Rotas/Dispersión (Diseminado)': formatSi(jd.bolsas_rotas_dispersion ?? item.bolsas_rotas_dispersion),
          'Acumulación': formatSi(jd.acumulacion ?? item.acumulacion),
          'Animal Muerto': formatSi(jd.animal_muerto ?? item.animal_muerto),
          'Bolsas': formatSi(jd.bolsas ?? item.bolsas),
          'Escombro': formatSi(jd.escombro ?? item.escombro),
          'Neumático': formatSi(jd.neumatico ?? item.neumatico),
          'Poda': formatSi(jd.poda ?? item.poda),
          'Voluminoso': formatSi(jd.voluminoso ?? item.voluminoso),
          'Arbol Altura Máxima': jd.arbol_altura_maxima ?? item.arbol_altura_maxima,
          'ID Frame': item.id_frame,
          'URL Frame': item.url_frame,
          'Nombre Video': item.name_video,
          'Nro Frame': item.nro_frame,
        };

        Object.keys(jd).forEach(key => {
          if (!exportedItem[key] && !key.endsWith('_')) {
            exportedItem[`JD: ${key}`] = jd[key] ?? '';
          }
        });

        Object.keys(exportedItem).forEach(k => {
          if (exportedItem[k] === null || exportedItem[k] === undefined) {
            exportedItem[k] = '';
          }
        });

        return exportedItem;
      });

      // Crea un objeto de hoja de cálculo para "Senales" con los datos mapeados
      const worksheetSenales = XLSX.utils.json_to_sheet(dataToExport);

      // Crea un libro de trabajo
      const workbook = XLSX.utils.book_new();

      // Agrega la hoja de cálculo de "Senales" al libro de trabajo
      XLSX.utils.book_append_sheet(workbook, worksheetSenales, 'Senales'); // Nombre de la pestaña

      // Crea un buffer de archivo
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

      // Crea un enlace de descarga
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      downloadLink.download = 'data-proyecto.xlsx';
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };

  const [visible, setVisible] = useState(false);

  const [visible2, setVisible2] = useState(false);
  const [visible3, setVisible3] = useState(false);
  const [visibleDataFrame, setVisibleDataFrame] = useState(false);
  const [visibleDataFramePoda, setVisibleDataFramePoda] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [senaleticaReclasificada, setSenaleticaReclasificada] = useState(null);
  const [descripcionReclasificada, setDescripcionReclasificada] = useState(null);
  const [severidadReclasificada, setSeveridadReclasificada] = useState(null);
  const [itemIdSenal, setItemIdSenal] = useState(null);
  const [tipoSenal, setTipoSenal] = useState(null);
  const [senaleticaDetectada, setSenaleticaDetectada] = useState(null);
  const [descripcionDetectada, setDescripcionDetectada] = useState(null);
  const [observacionesSenal, setObservacionesSenal] = useState(null);
  const [latitudSenal, setLatitudSenal] = useState(null);
  const [longitudSenal, setLongitudSenal] = useState(null);
  const [videoAsociadoSenal, setVideoAsociadoSenal] = useState(null);
  const [fechaDeteccionSenal, setFechaDeteccionSenal] = useState(null);
  const [puntokmSenal, setPuntokmSenal] = useState(null);
  const [sentidoSenal, setSentidoSenal] = useState(null);
  const [itemIdDano, setItemIdDano] = useState(null);
  const [tipoDano, setTipoDano] = useState(null);
  const [area, setArea] = useState(null);
  const [severidad, setSeveridad] = useState(null);
  const [observaciones, setObservaciones] = useState(null);
  const [latitudDano, setLatitudDano] = useState(null);
  const [longitudDano, setLongitudDano] = useState(null);
  const [carreteraAsociadaDano, setCarreteraAsociadaDano] = useState(null);
  const [fechaDeteccionDano, setfechaDeteccionDano] = useState(null);
  const [PuntokmDano, setPuntokmDano] = useState(null);
  const [sentidoDano, setsentidoDano] = useState(null);
  const [nombreProyecto1, setNombreProyecto1] = useState(null);
  const [fechaCreacion1, setFechaCreacion1] = useState(null);
  const [fechaInicio1, setFechaInicio1] = useState(null);
  const [fechaFin1, setFechaFin1] = useState(null);
  const [cliente1, setCliente1] = useState(null);
  const [operador1, setOperador1] = useState(null);
  const [descripcion1, setDescripcion1] = useState(null);
  const [activeKey, setActiveKey] = useState('2'); // Estado para el tab activo (string), '2' por defecto (Detecciones)
  const [buscarTexto, setBuscarTexto] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  // Entrada temporal para el buscador; la búsqueda real se activa con el botón
  const [searchInput, setSearchInput] = useState('');
  const [copiedRow, setCopiedRow] = useState(null); // Para indicar fila copiada temporalmente
  const [urlFrame, setUrlFrame] = useState(null);
  const [lastViewedRowId, setLastViewedRowId] = useState(null);
  const [focusMarkerId, setFocusMarkerId] = useState(null);
  const [forceShowAll, setForceShowAll] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);


  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageFisuras, setCurrentPageFisuras] = useState(1);
  const [activeSubFilter, setActiveSubFilter] = useState(null);
  const [currentPageSenales, setCurrentPageSenales] = useState(1); // Página actual para señales (empieza en 1)
  const [itemsPerPage] = useState(50); // O la cantidad que desees
  const [itemsPerPageFisuras] = useState(50); // O la cantidad que desees
  const mapRef = useRef(null);
  const [currentItems, setCurrentItems] = useState([]);
  const [allFilteredItems, setAllFilteredItems] = useState([]); // Nueva lista completa filtrada
  const [currentItemsFisuras, setCurrentItemsFisuras] = useState([]);
  const [totalPages, setTotalPages] = useState([]);
  const [totalPagesFisuras, setTotalPagesFisuras] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);

  const lines = [
    "La busqueda es por el campo:",
    "frame",
  ];

  const position = [-34.6001622, -58.5846608]

  // --- LÓGICA DE FILTRADO LOCAL ---
  useEffect(() => {
    // Intentar obtener las señales del lugar correcto (senales o result.results)
    const baseData = dataProyecto?.senales || dataProyecto?.result?.results;
    if (!dataProyecto || !baseData) return;

    // 1. Filtrar por Clase/Categoría
    const filtered = baseData.filter((m) => {
      const tipo = (m.tipo || '').toString().toLowerCase();
      const tipoEsp = (m.tipo_especifico || '').toString().toLowerCase();

      let categoryMatch = true;
      const targetFilter = claseSeleccionada || mapFilter;

      if (!targetFilter) {
        categoryMatch = false; // Por defecto no mostramos nada hasta elegir filtro
      } else {
        // 1. Filtrar por Categoría (Modular)
        const config = getCategoryConfig(targetFilter);
        categoryMatch = targetFilter === 'all' || config.regex.test(tipoEsp + tipo);

        if (!categoryMatch) return false;

        // 1.5 Filtrar por Sub-Filtro (Modular)
        if (activeSubFilter) {
          const subConfig = config.subFilters.find(f => f.id === activeSubFilter);
          if (subConfig) {
            if (!subConfig.check(m)) return false;
          }
        }
      }

      // 2. Filtrar por Búsqueda (ID, tipo, dirección)
      let searchMatch = true;
      if (buscarTexto) {
        const q = buscarTexto.toLowerCase();
        searchMatch =
          (m.id_frame && m.id_frame.toString().includes(q)) ||
          (m.id && m.id.toString().includes(q)) ||
          (m.tipo_especifico && m.tipo_especifico.toLowerCase().includes(q)) ||
          (m.tipo && m.tipo.toLowerCase().includes(q)) ||
          (m.direccion && m.direccion.toLowerCase().includes(q));
      }

      // 3. Filtrar por Prioridad
      let priorityMatch = true;
      if (priorityFilter !== 'all') {
        priorityMatch = calculatePriorityResult(m) === priorityFilter;
      }

      // 4. Filtrar por Empresa
      let companyMatch = true;
      if (companyFilter !== 'all') {
        companyMatch = calculateAssignedCompany(m) === companyFilter;
      }

      return categoryMatch && searchMatch && priorityMatch && companyMatch;
    });

    setAllFilteredItems(filtered); // Guardamos la lista completa filtrada

    // 3. Aplicar Paginación Local
    const total = filtered.length;
    setTotalPages(Math.ceil(total / itemsPerPage));

    const start = (currentPageSenales - 1) * itemsPerPage;
    const paginated = filtered.slice(start, start + itemsPerPage);

    setCurrentItems(paginated);
  }, [dataProyecto, mapFilter, claseSeleccionada, buscarTexto, activeSubFilter, currentPageSenales, itemsPerPage, priorityFilter, companyFilter]);

  // --- LÓGICA DE PAGINACIÓN LOCAL PARA FISURAS ---
  useEffect(() => {
    const baseData = dataProyecto?.fisuras || dataProyecto?.result?.results_2;
    if (!dataProyecto || !baseData) return;

    const total = baseData.length;
    setTotalPagesFisuras(Math.ceil(total / itemsPerPageFisuras));

    // currentPageFisuras se maneja como 1-indexed para consistencia con Senales
    const page = currentPageFisuras || 1;
    const start = (page - 1) * itemsPerPageFisuras;
    const paginated = baseData.slice(start, start + itemsPerPageFisuras);

    setCurrentItemsFisuras(paginated);
  }, [dataProyecto, currentPageFisuras, itemsPerPageFisuras]);

  // 🔹 Función para agregar un pequeño desplazamiento aleatorio
  const getRandomOffset = () => (Math.random() - 0.5) * 0.0003;

  const handleMarkerClick = useCallback((markerData) => {
    setLastViewedRowId(markerData.id);
    setData([{
      tipo: markerData.tipo,
      tipo_especifico: markerData.tipo_especifico,
      id_frame: markerData.id_frame,
      url_frame: markerData.url_frame,
      tipo_camara: markerData.tipo_camara,
      name_video: markerData.name_video,
      direccion: markerData.direccion,
      fecha_captura: markerData.fecha_captura
    }]);
  }, []);

  const handleMarkerClickWithCluster = useCallback((markerData1, e) => {
    handleMarkerClick(markerData1); // Ejecuta la función personalizada

    if (mapRef.current) {
      const map = mapRef.current;
      const zoom = map.getZoom();

      // 🔹 Si aún está agrupado, forzar zoom para separar los marcadores
      if (zoom < 17) {
        map.setZoom(17, { animate: true });
      }

      e.target.openPopup(); // 🔹 Abre el popup manualmente
    }
  }, [handleMarkerClick]);

  const handleCloseToast = (element) => {
    setShowSuccessToast(false);
  }
  const handleCloseToastError = (element) => {
    setShowErrorToast(false);
  }

  const handleOpenUnifiedDetail = useCallback((item) => {
    setSelectedItem(item);
    setLastViewedRowId(item.id);
    setUrlFrame(item.url_frame);

    setSenaleticaDetectada(item.tipo_especifico);
    setLatitudSenal(item.latitud);
    setLongitudSenal(item.longitud);
    setFechaDeteccionSenal(item.fecha_captura);
    setObservacionesSenal(item.descripcion);

    setVisible3(true);
  }, []);

  const handleNextItem = useCallback(() => {
    if (!selectedItem || !allFilteredItems.length) return;
    const currentIndex = allFilteredItems.findIndex(i => i.id === selectedItem.id);
    if (currentIndex !== -1 && currentIndex < allFilteredItems.length - 1) {
      handleOpenUnifiedDetail(allFilteredItems[currentIndex + 1]);
    }
  }, [selectedItem, allFilteredItems, handleOpenUnifiedDetail]);

  const handlePrevItem = useCallback(() => {
    if (!selectedItem || !allFilteredItems.length) return;
    const currentIndex = allFilteredItems.findIndex(i => i.id === selectedItem.id);
    if (currentIndex > 0) {
      handleOpenUnifiedDetail(allFilteredItems[currentIndex - 1]);
    }
  }, [selectedItem, allFilteredItems, handleOpenUnifiedDetail]);

  const handleVerImagen = useCallback((url_frame) => {
    setUrlFrame(url_frame);
    setVisible3(true);
  }, []);

  // cuando el usuario hace clic en el número de frame del widget a la derecha del mapa
  const handleFrameLinkClick = (frame) => {
    if (!frame) return;
    setSearchInput(frame);
    setBuscarTexto(frame);
    setCurrentPageSenales(1);
    fetchData2(1, currentPageFisuras, 0, claseSeleccionada, frame);
  };

  // handleMarkerClick moved to useCallback above

  const handleUpdateDanno = async () => {
    // e.preventDefault();
    // if (fechaRelevamiento != null) {
    //   fechaRelevamiento = selectedDate3.toISOString().slice(0, 19).replace('T', ' ');
    // }

    const formData = new FormData();
    formData.append('itemIdDano', itemIdDano);
    formData.append('area', area);
    formData.append('tipoDano', tipoDano);
    formData.append('tipoReclasificacionDano', severidadReclasificada);
    formData.append('severidad', severidad);
    formData.append('observaciones', observaciones);
    formData.append('latitudDano', latitudDano);
    formData.append('longitudDano', longitudDano);

    // formData.append('estadoDrenaje', CarreteraAsociadaDan);
    // formData.append('anchoCalzada', sentidoDano);


    // formData.append('estadoBordeCalzada', estadoBordeCalzada);
    // formData.append('tipoPavimento', tipoPavimento);

    try {
      const response = await apiClient.post(`/api/update/detection-damage`, formData);

      // Limpiar cache para que los cambios se vean reflejados en la siguiente carga
      clearDataCache(id);

      setVisible2(false);
      setShowSuccessToast(true);
      // Recargar datos suavemente o volver a cargar
      handlefind(mapFilter);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
      setShowErrorToast(true)
    } finally {
      setIsLoading(false); // Ocultar el spinner al finalizar la carga
    }
  };

  const handleAuditFeedback = async (formData) => {
    setIsLoading(true);
    try {
      // Usamos el endpoint de actualización para persistir el feedback de auditoría
      // Según el usuario, el backend recibe itemIdSenal, statusSenal y observacionesSenal
      const response = await apiClient.post(`/api/update/detection-sign`, formData);
      
      clearDataCache(id);
      setShowSuccessToast(true);
      handlefind(claseSeleccionada);
      // Opcional: No cerramos el modal para que el usuario vea que se guardó, 
      // o lo cerramos si se prefiere. Por ahora lo dejamos abierto para fluidez.
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
      console.error("Error al guardar auditoría:", error);
      setShowErrorToast(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateSennal = async () => {
    // e.preventDefault();
    // if (fechaRelevamiento != null) {
    //   fechaRelevamiento = selectedDate3.toISOString().slice(0, 19).replace('T', ' ');
    // }

    const formData = new FormData();
    formData.append('itemIdSenal', itemIdSenal);
    formData.append('tipoSenal', tipoSenal);
    formData.append('senaleticaDetectada', senaleticaDetectada);
    formData.append('senaleticaReclasificada', senaleticaReclasificada);
    formData.append('descripcionReclasificada', descripcionReclasificada);
    // formData.append('severidad', severidad);
    formData.append('observacionesSenal', observacionesSenal);
    formData.append('latitudSenal', latitudSenal);
    formData.append('longitudSenal', longitudSenal);

    try {
      const response = await apiClient.post(`/api/update/detection-sign`, formData);

      // Limpiar cache
      clearDataCache(id);

      setVisible(false);
      setShowSuccessToast(true);
      handlefind(claseSeleccionada);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      }
      setShowErrorToast(true)
    } finally {
      setIsLoading(false); // Ocultar el spinner al finalizar la carga
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    handlefind(null, controller.signal);
    return () => controller.abort();
  }, [id, token, handlefind]);

  // Limpiar estados de modales al cambiar de categoría, pero NO resetear la pestaña activa
  useEffect(() => {
    setVisible(false);
    setVisible2(false);
  }, [claseSeleccionada]);

  useEffect(() => {
    if (dataProyecto) {
      // Sincronizar marcadores del mapa
      setMarkersData(dataProyecto);

      setData(dataProyecto.senales || []);

      setNombreProyecto1(dataProyecto.proyecto ? dataProyecto.proyecto[0].nombre : null);
      setCliente1(dataProyecto.proyecto ? dataProyecto.proyecto[0].cliente : null);
      setOperador1(dataProyecto.proyecto ? dataProyecto.proyecto[0].operador : null);
      setDescripcion1(dataProyecto.proyecto ? dataProyecto.proyecto[0].descripcion : null);
      setFechaCreacion1(dataProyecto.proyecto ? dataProyecto.proyecto[0].fecha_creacion : null);
      setFechaInicio1(dataProyecto.proyecto ? dataProyecto.proyecto[0].fecha_inicio : null);
      setFechaFin1(dataProyecto.proyecto ? dataProyecto.proyecto[0].fecha_fin : null);

      if (dataProyecto.result) {
        setTotalPages(dataProyecto.result.pages || [1]);
        setTotalPagesFisuras(dataProyecto.result.pages_2 || [1]);
      }
    }
  }, [dataProyecto]);

  // Actualizar currentItems cuando cambia currentPage
  useEffect(() => {
    if (dataProyecto) {
      // Logic for currentItems is already in the main effect
    }
  }, [currentPage, dataProyecto, itemsPerPage]);

  // Actualizar currentItems cuando cambia currentPage
  useEffect(() => {
    if (dataProyecto) {
      // Logic for currentItemsFisuras is already in the main effect
    }
  }, [currentPageFisuras, dataProyecto, itemsPerPageFisuras]);

  // Removido useEffect redundante de markersData que causaba bucles involuntarios


  // useEffect(() => {
  //   if (mapRef.current) {
  //     mapRef.current.on("zoomend", () => {
  //       console.log("Zoom level:", mapRef.current.getZoom());
  //     });

  const handleEditDanno = (id) => {
    const baseData = dataProyecto?.fisuras || dataProyecto?.result?.results_2 || [];
    const item = baseData.find(i => i.id === id);
    if (item) {
      setItemIdDano(id);
      setLastViewedRowId(id);
      setArea(item.area);
      setTipoDano(item.tipo);
      setSeveridad(item.severidad);
      setSeveridadReclasificada(item.tipo_reclasificacion)
      setObservaciones(item.descripcion);
      setLatitudDano(item.latitud);
      setLongitudDano(item.longitud);
      setCarreteraAsociadaDano(item.carretera_asociada);
      setsentidoDano(item.sentido_carril);
      setfechaDeteccionDano(item.fecha_captura);
      setVisible2(true);
    }
  };


  /* const handleDatosFrame = (index, id) => {
    ...
  } */

  const fetchDataframe = useCallback(async (frameId) => {
    setIsLoading(true);
    try {
      const tokenData = localStorage.getItem('token');
      let token = tokenData;
      if (tokenData && (tokenData.startsWith('{') || tokenData.startsWith('['))) {
        try {
          const parsed = JSON.parse(tokenData);
          token = parsed.token || tokenData;
        } catch (e) {
          token = tokenData;
        }
      }

      if (!token) {
        throw new Error('Token no disponible en localStorage');
      }

      const response = await apiClient.get(`/api/get-data-frame/${frameId}`);

      if (response.status !== 200) {
        throw new Error("Error en la solicitud a la API");
      }
      console.log(response.data)
      console.log(response.data[0].frame_data)
      // setDataResults2(response.data.results_2)
      // setCurrentItemsFisuras(response.data.results_2)
      // setTotalPagesFisuras(response.data.pages_2)
      // setTotalPagesFisuras(response.data.pages_2)




    } catch (error) {
      if (error?.response?.status === 401) {
        handleUnauthorized();
      } else {
        console.error("Error al obtener los datos:", error);
      }
    } finally {
      setIsLoading(false);
    }
  }, [id]); // Dependencia de 'id'



  // Función para descargar la imagen
  const handleDownloadImage = (imageUrl) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = 'imagen_descargada.jpg'; // Puedes cambiar el nombre del archivo aquí
    link.click();
  };

  // Copiar dirección al portapapeles
  const handleCopyDireccion = (text, idx) => {
    if (!text) return;
    try {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      navigator.clipboard.writeText(text).then(() => {
        setCopiedRow(idx);
        copyTimeoutRef.current = setTimeout(() => setCopiedRow(null), 1500);
      }).catch((err) => {
        console.error('Error copiando al portapapeles:', err);
      });
    } catch (err) {
      console.error('Clipboard API no disponible:', err);
    }
  };

  // Abrir coordenadas en Google Maps
  const [manualObservations, setManualObservations] = useState('');
  const [showObservationInput, setShowObservationInput] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [isSavedObservations, setIsSavedObservations] = useState(false);
  const [currentFeedbackStatus, setCurrentFeedbackStatus] = useState(null);

  // Mapeo de estados de feedback a enteros para el backend
  const feedbackStatusMap = {
    'none': 0,
    'bien': 1,
    'mal': 3
  };

  // Sincronizar estado cuando cambia el item seleccionado (incluso por navegación < >)
  useEffect(() => {
    if (selectedItem) {
      // Priorizar descripcionSenal (nuevo), luego descripcion (anterior)
      setManualObservations(selectedItem.descripcionSenal || selectedItem.descripcion || '');
      
      // Buscar statusSenal (int), luego status_senal (int), luego status_feedback (legacy)
      let initialStatus = 0;
      if (selectedItem.statusSenal !== undefined && selectedItem.statusSenal !== null) {
        initialStatus = parseInt(selectedItem.statusSenal, 10);
      } else if (selectedItem.status_senal !== undefined && selectedItem.status_senal !== null) {
        initialStatus = parseInt(selectedItem.status_senal, 10);
      } else if (selectedItem.status_feedback) {
        initialStatus = feedbackStatusMap[selectedItem.status_feedback] || 0;
      }
      
      setCurrentFeedbackStatus(initialStatus);
      setShowObservationInput(!!selectedItem.descripcion || initialStatus !== 0);
    } else {
      setManualObservations('');
      setCurrentFeedbackStatus(0);
      setShowObservationInput(false);
    }
  }, [selectedItem]);

  const handleQuickFeedback = async (statusStr) => {
    const statusInt = feedbackStatusMap[statusStr] || 0;
    // Si cliqueamos el mismo, revertimos a 0 (toggle)
    const newStatus = currentFeedbackStatus === statusInt ? 0 : statusInt;
    
    setCurrentFeedbackStatus(newStatus);
    setIsSubmittingFeedback(true);
    
    const formData = new FormData();
    formData.append('itemIdSenal', lastViewedRowId);
    formData.append('statusSenal', newStatus); 
    formData.append('descripcionSenal', manualObservations);

    try {
      await apiClient.post(`/api/update/detection-sign`, formData);
      
      // Actualización Optimista
      if (selectedItem) {
        selectedItem.statusSenal = newStatus;
        selectedItem.status_senal = newStatus; // Compatibilidad
        selectedItem.descripcionSenal = manualObservations;
        // Mantenemos legacy por si acaso algún otro componente lo usa
        selectedItem.status_feedback = Object.keys(feedbackStatusMap).find(key => feedbackStatusMap[key] === newStatus);
      }
      
      setShowObservationInput(newStatus !== 0); 
      setIsSavedObservations(true);
      setShowSuccessToast(true);
      setTimeout(() => setIsSavedObservations(false), 2500);
    } catch (error) {
      console.error("Error en feedback rápido:", error);
      setShowErrorToast(true);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleSaveObservations = async () => {
    setIsSubmittingFeedback(true);
    const formData = new FormData();
    formData.append('itemIdSenal', lastViewedRowId);
    formData.append('statusSenal', currentFeedbackStatus);
    formData.append('descripcionSenal', manualObservations);

    try {
      await apiClient.post(`/api/update/detection-sign`, formData);
      if (selectedItem) {
        selectedItem.descripcionSenal = manualObservations;
        selectedItem.descripcion = manualObservations; // Compatibilidad
      }
      setIsSavedObservations(true);
      setShowSuccessToast(true);
      setTimeout(() => setIsSavedObservations(false), 2500);
    } catch (error) {
      setShowErrorToast(true);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleOpenInGoogleMaps = (lat, lng) => {
    if (!isValidCoordinate(lat, lng)) return;
    const query = encodeURIComponent(`${lat},${lng}`);
    const url = `https://www.google.com/maps/search/?api=1&query=${query}`;
    try {
      window.open(url, '_blank');
    } catch (err) {
      console.error('No se pudo abrir Google Maps:', err);
    }
  };


  const focusOnCoordinate = (lat, lng) => {
    if (mapRef.current && isValidCoordinate(lat, lng)) {
      mapRef.current.flyTo([parseFloat(lat), parseFloat(lng)], 18, { duration: 1.5 });
    }
  };

  const fetchData2ControllerRef = useRef(null);

  const fetchData2 = useCallback(async (pageSenales = 1, pageFisuras = 1, valor, claseSelect, searchIdFrame = '') => {
    // Cancel previous fetchData2 request
    if (fetchData2ControllerRef.current) {
      fetchData2ControllerRef.current.abort();
    }
    fetchData2ControllerRef.current = new AbortController();
    const signal = fetchData2ControllerRef.current.signal;

    setIsLoading(true);
    try {
      const tokenData = localStorage.getItem('token');
      let tokenValue = tokenData;
      if (tokenData && (tokenData.startsWith('{') || tokenData.startsWith('['))) {
        try {
          const parsed = JSON.parse(tokenData);
          tokenValue = parsed.token || tokenData;
        } catch (e) {
          tokenValue = tokenData;
        }
      }

      if (!tokenValue) {
        throw new Error('Token no disponible en localStorage');
      }

      const searchParam = searchIdFrame ? `&id_frame=${encodeURIComponent(searchIdFrame)}` : '';
      const response = await apiClient.get(`/results-new/${id}?page=${pageSenales}&per_page=${itemsPerPage}&claseSeleccionada=${claseSelect}${searchParam}`, { signal });

      if (response.status !== 200) {
        throw new Error("Error en la solicitud a la API");
      }

      if (valor !== 0) {
        setDataResults2(response.data.results_2)
        setCurrentItemsFisuras(response.data.results_2)
        setTotalPagesFisuras(response.data.pages_2)
      }
    } catch (error) {
      if (axios.isCancel(error) || error.name === 'AbortError') {
        console.log('fetchData2 request canceled');
      } else if (error?.response?.status === 401) {
        handleUnauthorized();
      } else {
        console.error("Error al obtener los datos:", error);
      }
    } finally {
      if (!signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [id, itemsPerPage, handleUnauthorized]);

  // Manejo de clic en la paginación para señales
  const handlePageClickSenales = (pageNumber) => {
    // console.log(pageNumber)
    // console.log('pageNumber')
    // console.log(dataResults)
    setCurrentPageSenales(pageNumber);
    // fetchData2(pageNumber, currentPageSenales, 0, claseSeleccionada, buscarTexto); // ELIMINADO: ahora es local
    setCurrentPage(pageNumber);
  };
  // Maneja clics en los cards superiores para filtrar el mapa y la tabla
  const handleCardFilter = (mapValue, claseValue) => {
    try {
      if (mapValue === 'all') {
        const baseData = dataProyecto?.senales || dataProyecto?.result?.results || [];
        if (baseData.length >= 4000 && !forceShowAll) {
          setIsConfirmModalOpen(true);
          return;
        }
      }
      setMapFilter(mapValue);
      setClaseSeleccionada(claseValue);
      setActiveSubFilter(null); // Limpiar sub-filtro al cambiar categoría principal
      setCurrentPageSenales(1);
      setActiveKey('2');
    } catch (err) {
      console.error('Error en handleCardFilter:', err);
    }
  };

  const handleSubFilterClick = (subId) => {
    setActiveSubFilter(prev => prev === subId ? null : subId);
    setCurrentPageSenales(1);
    setActiveKey('2');
  };

  const confirmShowAll = () => {
    setForceShowAll(true);
    setMapFilter('all');
    setClaseSeleccionada('all');
    setCurrentPageSenales(1);
    setIsConfirmModalOpen(false);
    setActiveKey('2');
  };


  // Manejo de clic en la paginación para fisuras
  const handlePageClickFisuras2 = (pageNumber) => {
    // console.log(pageNumber)
    // console.log('pageNumber2')
    setCurrentPageFisuras(pageNumber);
    // Vuelve a buscar datos con la nueva página; incluir la búsqueda actual (buscarTexto)
    fetchData2(pageNumber, currentPageFisuras, 1, claseSeleccionada, buscarTexto); // Vuelve a buscar datos con la nueva página

    setCurrentPageFisuras(pageNumber);
  };

  // Helper para renderizar paginación con el patrón: << 1 ... p-1 p p+1 ... N >>
  const renderPagination = (totalPagesCount, currentPageNumber, onPageClick) => {
    const total = parseInt(totalPagesCount, 10) || 0;
    const current = parseInt(currentPageNumber, 10) || 1;
    if (total <= 1) return null;

    const items = [];

    // Prev (<<)
    items.push(
      <CPaginationItem key="prev" disabled={current === 1} onClick={() => onPageClick(Math.max(1, current - 1))}>
        {'<'}
      </CPaginationItem>
    );

    // First page + left ellipsis
    if (current > 2) {
      items.push(
        <CPaginationItem key={1} active={1 === current ? 'true' : undefined} onClick={() => onPageClick(1)}>
          {1}
        </CPaginationItem>
      );
      if (current > 3) {
        items.push(
          <CPaginationItem key="start-ellipsis" disabled>
            ...
          </CPaginationItem>
        );
      }
    }

    // Pages around current: current-1, current, current+1
    const start = Math.max(1, current - 1);
    const end = Math.min(total, current + 1);
    for (let p = start; p <= end; p++) {
      items.push(
        <CPaginationItem key={p} active={p === current ? 'true' : undefined} onClick={() => onPageClick(p)}>
          {p}
        </CPaginationItem>
      );
    }

    // Right ellipsis + last page
    if (current < total - 1) {
      if (current < total - 2) {
        items.push(
          <CPaginationItem key="end-ellipsis" disabled>
            ...
          </CPaginationItem>
        );
      }
      items.push(
        <CPaginationItem key={total} active={total === current ? 'true' : undefined} onClick={() => onPageClick(total)}>
          {total}
        </CPaginationItem>
      );
    }

    // Next (>>)
    items.push(
      <CPaginationItem key="next" disabled={current === total} onClick={() => onPageClick(Math.min(total, current + 1))}>
        {'>'}
      </CPaginationItem>
    );

    return (
      <CPagination aria-label="Page navigation example" className="cursor-pointer">
        {items}
      </CPagination>
    );
  };

  return (
    <div className="detail-index-main-wrapper" style={{ overflowX: 'hidden' }}>
      <CRow className='margin-row-contenedor align-items-center mb-4'>
        <CCol xs='12' className='d-flex align-items-center justify-content-between mb-3 bg-white p-3 rounded-4 shadow-sm border'>
          <div className="d-flex align-items-center">
            <CButton
              color="light"
              onClick={() => navigate('/dashboard')}
              className="me-4 modern-back-button border-0 shadow-none hover-bg-light"
              style={{ borderRadius: '12px', fontWeight: '600', padding: '8px 16px', color: '#4a5568' }}
            >
              <CIcon icon={cilArrowLeft} className="me-2" />
              Volver
            </CButton>
            <div className="rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm" style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)' }}>
              <CIcon icon={cilMap} size="xl" style={{ '--ci-primary-color': 'white' }} />
            </div>
            <div>
              <h3 className='mb-0 fw-bold' style={{ color: '#131133', letterSpacing: '-0.5px' }}>Detalle de Proyecto</h3>
              <span className="text-muted fw-semibold" style={{ fontSize: '0.85rem' }}>Visualización y gestión de detecciones</span>
            </div>
          </div>
          
          <div className="d-flex align-items-center">
          {isLoading && dataProyecto && (
            <div className="ms-3 d-flex align-items-center text-primary small fw-semibold">
              <CSpinner size="sm" variant="grow" className="me-2" />
              Buscando actualizaciones...
            </div>
          )}
          {fetchError && !dataProyecto && (
            <div className="ms-3 d-flex align-items-center">
              <CButton color="danger" size="sm" className="px-3" onClick={() => handlefind(claseSeleccionada)}>
                <CIcon icon={cilReload} className="me-2" />
                {fetchError} — Reintentar
              </CButton>
            </div>
          )}
          </div>
        </CCol>

        <div className="grid-container mt-4">
          <MapSection
            isLoading={isLoading}
            position={position}
            mapRef={mapRef}
            markersData={markersData}
            mapFilter={mapFilter}
            activeSubFilter={activeSubFilter}
            lastViewedRowId={lastViewedRowId}
            focusMarkerId={focusMarkerId}
            handleMarkerClickWithCluster={handleMarkerClickWithCluster}
            handleOpenUnifiedDetail={handleOpenUnifiedDetail}
            forceShowAll={forceShowAll}
          />



          <CCard className='video-container-card'>
            <Widgets
              data={data}
              onFrameClick={handleFrameLinkClick}
              onOpenDetails={() => {
                const baseData = dataProyecto?.senales || dataProyecto?.result?.results || [];
                const currentItem = baseData.find(i => i.id_frame === data[0]?.id_frame);
                if (currentItem) handleOpenUnifiedDetail(currentItem);
              }}
            />
          </CCard>
        </div>

        <StatsCards
          dataProyecto={dataProyecto}
          mapFilter={mapFilter}
          handleCardFilter={handleCardFilter}
          isLoading={isLoading}
          activeSubFilter={activeSubFilter}
          handleSubFilterClick={handleSubFilterClick}
        />
        <div className="grid-container-2 mt-4">
          <div className="grid-item">
            <CCard className='video-container-card'>
              <div className="tab-list-container p-3">
                <CRow className="g-3 align-items-stretch">
                  <CCol xs={12}>
                    <BuscadorSenales
                      searchInput={searchInput}
                      setSearchInput={setSearchInput}
                      buscarTexto={buscarTexto}
                      setBuscarTexto={setBuscarTexto}
                      mapFilter={mapFilter}
                      activeSubFilter={activeSubFilter}
                      handleSubFilterClick={handleSubFilterClick}
                      setCurrentPageSenales={setCurrentPageSenales}
                      handleDownload={handleDownload}
                      isLoading={isLoading}
                      data={dataProyecto?.senales || dataProyecto?.result?.results || []}
                      priorityFilter={priorityFilter}
                      setPriorityFilter={setPriorityFilter}
                      companyFilter={companyFilter}
                      setCompanyFilter={setCompanyFilter}
                    />
                  </CCol>
                </CRow>


                <CTabs key={`main-tabs-${id}`} activeItemKey={activeKey} onActiveKeyChange={(k) => setActiveKey(k.toString())}>
                  <CTabList variant="underline-border" className='tablist-map-component mt-4 mb-2' style={{ position: 'relative', zIndex: 1000 }}>
                    <CTab 
                      itemKey="1" 
                      onClick={(e) => { e.preventDefault(); setActiveKey('1'); }}
                      style={{ cursor: 'pointer' }}
                    >
                      Información de Proyecto
                    </CTab>
                    <CTab 
                      itemKey="2" 
                      onClick={(e) => { e.preventDefault(); setActiveKey('2'); }}
                      style={{ cursor: 'pointer' }}
                    >
                      Detecciones
                    </CTab>
                    <CTab 
                      itemKey="3" 
                      onClick={(e) => { e.preventDefault(); setActiveKey('3'); }}
                      style={{ cursor: 'pointer' }}
                    >
                      Resumen Ejecutivo
                    </CTab>
                  </CTabList>
                  <CTabContent>
                    <CTabPanel className="py-4" itemKey="1">
                      <ProjectInfoCard
                        isLoading={isLoading}
                        dataProyecto={dataProyecto}
                        id={id}
                        nombreProyecto={nombreProyecto1}
                        cliente={cliente1}
                        operador={operador1}
                        descripcion={descripcion1}
                        fechaCreacion={fechaCreacion1}
                        fechaInicio={fechaInicio1}
                        fechaFin={fechaFin1}
                      />
                    </CTabPanel>

                    <CTabPanel className="py-2" itemKey="2">
                      <DetectionsTable
                        isLoading={isLoading}
                        dataProyecto={dataProyecto}
                        currentItems={currentItems}
                        lastViewedRowId={lastViewedRowId}
                        claseSeleccionada={claseSeleccionada}
                        handleOpenUnifiedDetail={handleOpenUnifiedDetail}
                        handleVerImagen={handleVerImagen}
                        setFocusMarkerId={setFocusMarkerId}
                        setLastViewedRowId={setLastViewedRowId}
                        focusOnCoordinate={focusOnCoordinate}
                        handleCopyDireccion={handleCopyDireccion}
                        copiedRow={copiedRow}
                        handleOpenInGoogleMaps={handleOpenInGoogleMaps}
                        mapFilter={mapFilter}
                      />
                      {currentItems.length > 0 && (
                        <div className="d-flex justify-content-center mt-3">
                          {renderPagination(totalPages, currentPageSenales, handlePageClickSenales)}
                        </div>
                      )}
                    </CTabPanel>

                    <CTabPanel className="py-3" itemKey="3" style={{ minHeight: '500px' }}>
                      <ExecutiveSummary
                        data={dataProyecto?.senales || dataProyecto?.result?.results || []}
                        mapFilter={mapFilter || 'all'}
                      />
                    </CTabPanel>
                  </CTabContent>
                </CTabs>
              </div>
            </CCard>
          </div>
        </div>
      </CRow>



      {/* Toasts */}
      {showSuccessToast && (
        <CToast visible={true} autohide={true} delay={3000} className="text-white backgroundcolor-toast-exito" style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 3000 }}>
          <div className="d-flex"><CToastBody>Operación completada con éxito.</CToastBody><CToastClose onClick={handleCloseToast} white /></div>
        </CToast>
      )}
      {showErrorToast && (
        <CToast visible={true} autohide={true} delay={3000} className="text-white backgroundcolor-toast-error" style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 3000 }}>
          <div className="d-flex"><CToastBody>Ha ocurrido un error inesperado.</CToastBody><CToastClose onClick={handleCloseToastError} white /></div>
        </CToast>
      )}



      <CModal
        alignment="center"
        scrollable
        visible={visible2}
        onClose={() => setVisible2(false)}
        aria-labelledby="VerticallyCenteredExample"
      >
        <CModalContent >
          <CModalHeader style={{ '--cui-modal-header-border-color': 'white' }}>
            <CModalTitle className='letra-titulo-modal' id="VerticallyCenteredExample">Edición de Daño</CModalTitle>
          </CModalHeader>
          <CModalBody>

            <div className="mb-3 letra-contenido-2">
              <CRow>
                <CCol xs={6}>
                  <CFormLabel htmlFor="tipodano">Tipo</CFormLabel>
                  <CFormInput disabled className='letra-contenido-modal-input' size="sm" id="tipodano" placeholder="Ingrese tipo" value={tipoDano} onChange={(e) => setTipoDano(e.target.value)} />
                </CCol>
                <CCol xs={6}>
                  <CFormLabel htmlFor="area">Area</CFormLabel>
                  <CFormInput className='letra-contenido-modal-input' size="sm" id="area" placeholder="Ingrese area" value={area} onChange={(e) => setArea(e.target.value)} />
                </CCol>
              </CRow>
            </div>

            <div className="mb-3 letra-contenido-2">
              <CRow>
                <CCol xs={6}>
                  <CFormLabel htmlFor="severidad">Severidad</CFormLabel>
                  <CFormInput className='letra-contenido-modal-input' size="sm" id="severidad" placeholder="Ingrese Severidad" value={severidad} onChange={(e) => setSeveridad(e.target.value)} />
                </CCol>
                <CCol xs={6}>
                  <CFormSelect size="sm"
                    className='letra-contenido-modal-input'
                    label="Severidad Reclasificada"
                    value={severidadReclasificada}
                    onChange={(e) => setSeveridadReclasificada(e.target.value)}>
                    <option className='fuente-letra-oscura' >Seleccione</option>
                    <option className='fuente-letra-oscura' value="T02">T2</option>
                    <option className='fuente-letra-oscura' value="T04">T4</option>
                    <option className='fuente-letra-oscura' value="T06">T6</option>
                    <option className='fuente-letra-oscura' value="T08">T8</option>
                    <option className='fuente-letra-oscura' value="T10">T10</option>
                  </CFormSelect>
                </CCol>
              </CRow>
            </div>

            <div className="mb-3 letra-contenido-2">
              <CRow>
                <CCol xs={12}>
                  <CFormLabel htmlFor="observacionesdano">Observaciones</CFormLabel>
                  <CFormTextarea
                    className='letra-contenido-modal-input'
                    id="observacionesdano"
                    placeholder="Escribe comentarios u observaciones"
                    value={observaciones}
                    onChange={(e) => setObservaciones(e.target.value)}
                    rows={4}
                  ></CFormTextarea>
                </CCol>


              </CRow>
            </div>

            <div className="mb-3 letra-contenido-2">
              <CRow>
                <CCol xs={6}>
                  <CFormLabel htmlFor="latituddano">Latitud</CFormLabel>
                  <CFormInput className='letra-contenido-modal-input' size="sm" id="latituddano" placeholder="Ingrese latitud" value={latitudDano} onChange={(e) => setLatitudDano(e.target.value)} />
                </CCol>
                <CCol xs={6}>
                  <CFormLabel htmlFor="longituddano">Longitud</CFormLabel>
                  <CFormInput className='letra-contenido-modal-input' size="sm" id="longituddano" placeholder="Ingrese longitud" value={longitudDano} onChange={(e) => setLongitudDano(e.target.value)} />
                </CCol>
              </CRow>
            </div>
            <div className="mb-3 letra-contenido-2">
              <CRow>
                <CCol xs={6}>
                  <CFormLabel htmlFor="carreteraasociadadano">Carretera asociada</CFormLabel>
                  <CFormInput disabled className='letra-contenido-modal-input' size="sm" id="carreteraasociadadano" placeholder="Ingrese carretera" value={carreteraAsociadaDano} onChange={(e) => setCarreteraAsociadaDano(e.target.value)} />
                </CCol>
                <CCol xs={6}>
                  <CFormLabel htmlFor="fechadetecciondano">Fecha de detección</CFormLabel>
                  <CFormInput disabled className='letra-contenido-modal-input' size="sm" id="fechadetecciondano" placeholder="Ingrese fecha" value={fechaDeteccionDano} onChange={(e) => setfechaDeteccionDano(e.target.value)} />
                </CCol>
              </CRow>
            </div>

            <div className="mb-3 letra-contenido-2">
              <CRow>
                <CCol xs={6}>
                  <CFormLabel htmlFor="puntokmDano">Punto KM</CFormLabel>
                  <CFormInput disabled className='letra-contenido-modal-input' size="sm" id="puntokmDano" placeholder="Ingrese km" value={PuntokmDano} onChange={(e) => setPuntokmDano(e.target.value)} />
                </CCol>
                <CCol xs={6}>
                  <CFormLabel htmlFor="sentidoDano">Sentido</CFormLabel>
                  <CFormInput disabled className='letra-contenido-modal-input' size="sm" id="sentidoDano" placeholder="Ingrese sentido" value={sentidoDano} onChange={(e) => setsentidoDano(e.target.value)} />
                </CCol>
              </CRow>
            </div>

          </CModalBody>
          <CModalFooter style={{ '--cui-modal-footer-border-color': 'white' }}>
            <CButton className='btn-salir-footer' onClick={() => setVisible2(false)}>Cancelar</CButton>
            <CButton className='btn-next-footer' onClick={() => handleUpdateDanno()}>Guardar</CButton>
          </CModalFooter>
        </CModalContent>
      </CModal>
      <UnifiedDetailModal
        visible={visible3}
        onClose={() => setVisible3(false)}
        item={selectedItem}
        handlePrev={handlePrevItem}
        handleNext={handleNextItem}
        handleDownloadImage={handleDownloadImage}
        handleOpenInGoogleMaps={handleOpenInGoogleMaps}
        handleFlyTo={(lat, lng) => {
          setVisible3(false);
          if (lat && lng) {
            const mapElement = document.querySelector('.grid-container .map-container');
            if (mapElement) mapElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setTimeout(() => focusOnCoordinate(lat, lng), 500);
          }
        }}
        latitudSenal={latitudSenal}
        longitudSenal={longitudSenal}
        senaleticaDetectada={senaleticaDetectada}
        lastViewedRowId={lastViewedRowId}
        fechaDeteccionSenal={fechaDeteccionSenal}
        urlFrame={urlFrame}
        mapFilter={mapFilter}
        onSave={handleAuditFeedback}
        currentFeedbackStatus={currentFeedbackStatus}
        setCurrentFeedbackStatus={setCurrentFeedbackStatus}
        manualObservations={manualObservations}
        setManualObservations={setManualObservations}
        isSubmittingFeedback={isSubmittingFeedback}
        isSavedObservations={isSavedObservations}
        showObservationInput={showObservationInput}
        setShowObservationInput={setShowObservationInput}
        handleQuickFeedback={handleQuickFeedback}
        handleSaveObservations={handleSaveObservations}
      />

      <CModal visible={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)} alignment="center">
        <CModalHeader className="bg-warning text-dark border-0">
          <CModalTitle className="fw-bold">⚠️ Aviso de Rendimiento</CModalTitle>
        </CModalHeader>
        <CModalBody className="py-4 text-center">
          <div className="mb-3 h5 fw-semibold">
            Hay más de 4,000 detecciones en este proyecto.
          </div>
          <p className="text-muted">
            Mostrar todas simultáneamente en el mapa puede causar lentitud o "lag" en tu navegador.
          </p>
          <div className="fw-bold text-dark mt-2">
            ¿Deseas continuar y mostrarlas todas?
          </div>
        </CModalBody>
        <CModalFooter className="border-0 justify-content-center pb-4">
          <CButton color="secondary" variant="ghost" onClick={() => setIsConfirmModalOpen(false)} className="px-4">
            Cancelar
          </CButton>
          <CButton color="warning" onClick={confirmShowAll} className="px-5 fw-bold text-dark">
            Sí, mostrar todo
          </CButton>
        </CModalFooter>
      </CModal>
    </div>
  );
}




